import { faker } from '@faker-js/faker';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Material UI
import { useTheme } from '@mui/material/styles';
import { Grid, Container, Typography } from '@mui/material';

// Components
import Page from '../components/Page';
import Iconify from '../components/Iconify';

// Sections
import {
  AppWidgetSummary,
  AppWebsiteVisits,
  AppCurrentVisits,
  AppConversionRates,
  AppNewsUpdate,
  AppOrderTimeline,
  AppTrafficBySite,
  AppTasks,
  AppCurrentSubject
} from '../sections/@dashboard/app';

import { authState } from '../../../Redux/authSlice';
import { useLocation } from 'react-router-dom';

export const CoreApp = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const useOldDb = true; // toggle to true when ready

  const auth = useSelector(authState);
  console.log("Core State: ", { auth });

  const [caseStats, setCaseStats] = useState({
    inprogress: 0,
    open: 0,
    pending: 0,
    closed: 0,
  });

  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);

  const fetchBlogs = async () => {
    try {
      const response = await fetch('https://ipms.insollex.com/api/post');
      const data = await response.json();
      setBlogs(data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoadingBlogs(false);
    }
  };

  const fetchNewDbStats = async () => {
    try {
      const response = await fetch(`https://ipms.insollex.com/api/legal-matter/status?t=${Date.now()}`);
      if (!response.ok) throw new Error(`New DB failed with status ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('❌ Error fetching NEW DB case stats:', error);
      return {};
    }
  };
  
  const fetchOldDbStats = async () => {
    try {
      // const response = await fetch(`https://ipms.insollex.com/api/old-database/estates/status?t=${Date.now()}`);
      // if (!response.ok) throw new Error(`Old DB failed with status ${response.status}`);
      // return await response.json();
      return { inprogress: 0, open: 0, pending: 0, closed: 0 };
    } catch (error) {
      console.error('❌ Error fetching OLD DB case stats:', error);
      return {};
    }
  };
  
  
  const fetchCaseStats = async () => {
    setLoading(true);
    try {
      const promises = [fetchNewDbStats()];
      if (useOldDb) promises.push(fetchOldDbStats());
  
      const results = await Promise.all(promises);
      const newDbData = results[0];
      const oldDbData = useOldDb ? results[1] : { inprogress: 0, open: 0, pending: 0, closed: 0 };
  
      const combinedStats = {
        inprogress: (newDbData.inprogress || 0) + (oldDbData.inprogress || 0),
        open: (newDbData.open || 0) + (oldDbData.open || 0),
        pending: (newDbData.pending || 0) + (oldDbData.pending || 0),
        closed: (newDbData.closed || 0) + (oldDbData.closed || 0),
      };
  
      setCaseStats(combinedStats);
      console.log("Fetched Case Stats:", combinedStats);
    } catch (error) {
      console.error("Error fetching case stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth === undefined || auth === null) return; // Wait until auth is ready
  
    if (!auth) {
      console.log('Not authenticated, redirecting...');
      navigate('/', { replace: true });
      return;
    }
  
    console.log('Auth verified, fetching stats...');
    fetchCaseStats();
    fetchBlogs();
  }, [auth, navigate, location.pathname]);

  if (loading || loadingBlogs) {
    return (
      <Page title="Legal Dashboard">
        <Container maxWidth="xl">
          <Typography variant="h6" align="center">Loading data...</Typography>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Legal Dashboard">
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          {/* In Progress Cases Widget */}
          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary
              title="In Progress Cases"
              total={caseStats.inprogress}
              icon={'fa6-solid:hourglass-half'}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * In Progress Estate cases across departments. Data is regularly updated.
            </Typography>
          </Grid>

          {/*Completed Cases Widget*/}
          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary
              title="Open Cases"
              total={caseStats.open}
              color="info"
              icon={'a6-solid:gavel'}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Total number of Estate cases resolved successfully.
            </Typography>
          </Grid>

          {/* Pending Cases Widget */}
          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary
              title="Pending Cases"
              total={caseStats.pending}
              color="warning"
              icon={'fa6-solid:hourglass-half'}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Pending cases awaiting further actions or court dates.
            </Typography>
          </Grid>

          {/* Closed Cases Widget */}
          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary
              title="Closed Cases"
              total={caseStats.closed}
              color="error"
              icon={'fa6-solid:gavel'}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Estate rulings received across various jurisdictions.
            </Typography>
          </Grid>

          {/* The rest of the sections with fake data */}

          <Grid item xs={12} md={6} lg={8}>
            <AppWebsiteVisits
              title="Case Success Rate"
              subheader="(+13%) improvement this year"
              chartLabels={['Q1', 'Q2', 'Q3', 'Q4']}
              chartData={[ 
                { name: 'Criminal Cases', type: 'column', fill: 'solid', data: [35, 42, 51, 60] },
                { name: 'Civil Cases', type: 'area', fill: 'gradient', data: [65, 58, 52, 50] },
                { name: 'Family Cases', type: 'line', fill: 'solid', data: [50, 48, 55, 60] },
              ]}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Success rate is based on resolved cases in different legal areas.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <AppCurrentVisits
              title="Cases by Type"
              chartData={[
                { label: 'Criminal', value: 120 },
                { label: 'Civil', value: 150 },
                { label: 'Family', value: 60 },
                { label: 'Corporate', value: 90 },
              ]}
              chartColors={[
                theme.palette.primary.main,
                theme.palette.chart.blue[0],
                theme.palette.chart.violet[0],
                theme.palette.chart.yellow[0],
              ]}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Breakdown of active cases by their respective legal categories.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} lg={8}>
            <AppConversionRates
              title="Legal Team Performance"
              subheader="(+8%) growth compared to last year"
              chartData={[
                { label: 'Team A', value: 400 },
                { label: 'Team B', value: 430 },
                { label: 'Team C', value: 448 },
                { label: 'Team D', value: 470 },
                { label: 'Team E', value: 540 },
              ]}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Legal team performance based on case completion and client satisfaction.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <AppCurrentSubject
              title="Legal Areas Focused On"
              chartLabels={['Criminal Law', 'Civil Law', 'Family Law', 'Corporate Law']}
              chartData={[
                { name: 'Team A', data: [80, 90, 60, 70] },
                { name: 'Team B', data: [70, 60, 50, 85] },
                { name: 'Team C', data: [55, 65, 50, 60] },
              ]}
              chartColors={[...Array(4)].map(() => theme.palette.text.secondary)}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Legal teams are specialized in different law sectors. Performance varies by area.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} lg={8}>
  <AppNewsUpdate
    title="Legal News Update"
    list={
      blogs && Array.isArray(blogs)
        ? blogs
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Sort by latest
            .slice(0, 4) // Take the latest 4 blogs
            .map((blog) => ({
              id: blog.id,
              title: blog.title,
              description: blog.content
                ? blog.content.split('\n')[0]
                : "No description available.",
              image: blog.imageUrl ? blog.imageUrl : faker.image.abstract(),
              postedAt: new Date(blog.createdAt),
            }))
        : []
    }
    onViewAllClick={() => navigate('/blog')} // Navigate to the /blogs page when View All is clicked
  />
  <Typography variant="body2" color="textSecondary" align="center">
    * Latest updates from the legal field. Stay informed with changes in law.
  </Typography>
</Grid>
          <Grid item xs={12} md={6} lg={4}>
            <AppOrderTimeline
              title="Court Case Timelines"
              list={[...Array(5)].map((_, index) => ({
                id: faker.datatype.uuid(),
                title: `Case #${index + 1}: ${['Pending', 'In Progress', 'Resolved', 'Appealed'][index % 4]}`,
                type: `status${index + 1}`,
                time: faker.date.past(),
              }))}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Case timelines based on recent court activities and statuses.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <AppTrafficBySite
              title="Legal Sources"
              list={[
                { name: 'Court Records', value: 4321, icon: <Iconify icon={'fa6-solid:file-alt'} color="#003366" width={32} height={32} /> },
                { name: 'Client Consultations', value: 1243, icon: <Iconify icon={'fa6-solid:headset'} color="#006400" width={32} height={32} /> },
                { name: 'Legal Research', value: 890, icon: <Iconify icon={'fa6-solid:book-open'} color="#FFD700" width={32} height={32} /> },
                { name: 'Law Publications', value: 312, icon: <Iconify icon={'fa6-solid:newspaper'} color="#8B0000" width={32} height={32} /> },
              ]}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Legal source traffic based on cases, research, publications, and consultations.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} lg={8}>
            <AppTasks
              title="Pending Legal Tasks"
              list={[
                { id: '1', label: 'Review Criminal Case #4532' },
                { id: '2', label: 'Prepare for Family Case #1234' },
                { id: '3', label: 'Client Consultation for Civil Case #202' },
                { id: '4', label: 'Draft Response for Corporate Case #765' },
                { id: '5', label: 'Research Precedents for Appeal Case #234' },
              ]}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Pending tasks to be completed by legal teams to maintain case progression.
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};
